import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, X, Lightning } from '@phosphor-icons/react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import SEO from '../components/SEO';
import { SectionHero, SectionHeading, StatsRow, CTABanner, ProcessFlowSVG } from '../components/SiteBlocks';
import { OFFSHORE_MODELS, OFFSHORE_COMPARISON, STATS } from '../data/content';
import { Button } from '../components/ui/button';

export default function OffshoreOutsourcingPage() {
  return (
    <div className="min-h-screen" style={{ background: 'var(--background)', color: 'var(--text-primary)' }}>
      <SEO
        path="/offshore-outsourcing"
        title="Offshore Software Development from India — Hybrid AI + Senior Engineers"
        description="Hire senior software engineers from India — augmented by 8 specialist AI agents. 50-70% cost savings vs US/UK rates. ODC, staff augmentation, fixed-price projects, managed services."
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Offshore Software Development Services",
          "provider": { "@type": "Organization", "name": "XceedAgents.ai" },
          "areaServed": "Worldwide",
          "description": "AI-augmented offshore development from India: ODC, staff augmentation, project outsourcing, managed services.",
        }}
      />
      <Navbar />

      <SectionHero
        eyebrow="Offshore Outsourcing · India"
        title="Build with India's top 1% — augmented by AI."
        subtitle="Senior engineers from India + 8 specialist AI agents per developer. 50-70% lower cost than US/UK rates. 60% faster delivery than traditional offshore. You stay in control. We make it happen."
        testId="offshore-hero"
      >
        <div className="flex flex-wrap gap-3">
          <Link to="/start-project">
            <Button className="h-12 px-7 font-semibold hover-lift gap-2" style={{ background: 'var(--primary-accent)', color: '#fff' }} data-testid="offshore-get-quote">
              Request for Proposal <ArrowRight size={16} weight="bold" />
            </Button>
          </Link>
          <Link to="/contact">
            <Button variant="outline" className="h-12 px-7 font-semibold" style={{ borderColor: '#fff', color: '#fff' }} data-testid="offshore-talk-sales">
              Talk to Sales
            </Button>
          </Link>
        </div>
      </SectionHero>

      <StatsRow stats={[
        { value: '$45/hr', label: 'Starting hourly rate' },
        { value: '60%', label: 'Cost savings vs onshore' },
        { value: '2-3 wk', label: 'Time to first engineer' },
        { value: '500+', label: 'Engineers onboarded' },
      ]} />

      {/* ENGAGEMENT MODELS */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading
            eyebrow="Engagement Models"
            title="Pick the model that fits your team and timeline."
            subtitle="From dedicated long-term teams to plug-and-play senior engineers — we offer every flavor of offshore engagement, with AI augmentation built in."
          />
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            {OFFSHORE_MODELS.map((m, i) => (
              <motion.div
                key={m.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="p-7 lg:p-8 rounded-2xl border bg-white"
                style={{ borderColor: 'var(--border)' }}
                data-testid={`offshore-model-${m.id}`}
              >
                <div className="text-xs font-semibold tracking-[0.2em] uppercase mb-3" style={{ color: 'var(--primary-accent)' }}>Model {i + 1}</div>
                <h3 className="font-heading text-2xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>{m.name}</h3>
                <p className="text-base leading-relaxed mb-1" style={{ color: 'var(--text-primary)' }}>{m.tagline}</p>
                <p className="text-base leading-relaxed mb-5" style={{ color: 'var(--text-secondary)' }}>{m.summary}</p>
                <div className="text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: 'var(--text-muted)' }}>Ideal for</div>
                <p className="text-sm mb-5" style={{ color: 'var(--text-primary)' }}>{m.ideal_for}</p>
                <div className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: 'var(--text-muted)' }}>Benefits</div>
                <ul className="space-y-2 mb-5">
                  {m.benefits.map((b, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm" style={{ color: 'var(--text-primary)' }}>
                      <CheckCircle weight="fill" size={16} className="mt-0.5 flex-shrink-0" style={{ color: 'var(--primary-accent)' }} />
                      {b}
                    </li>
                  ))}
                </ul>
                <div className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: 'var(--text-muted)' }}>Timeline</div>
                <p className="text-sm font-semibold" style={{ color: 'var(--primary-accent)' }}>{m.timeline}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW WE COMPARE */}
      <section className="py-20 lg:py-24" style={{ background: 'var(--surface)' }}>
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading
            eyebrow="How We Compare"
            title="XceedAgents vs traditional offshore vs onshore."
            subtitle="Most offshore firms sell hours. We sell outcomes. Most onshore firms sell senior engineers. We sell senior engineers augmented with 8 specialist AI agents — at half the cost."
          />
          <div className="mt-12 overflow-x-auto rounded-2xl border bg-white" style={{ borderColor: 'var(--border)' }}>
            <table className="w-full text-sm">
              <thead style={{ background: 'var(--surface)' }}>
                <tr>
                  <th className="text-left p-4 font-semibold" style={{ color: 'var(--text-secondary)' }}>Feature</th>
                  <th className="text-left p-4 font-bold" style={{ color: 'var(--primary-accent)' }}>XceedAgents</th>
                  <th className="text-left p-4 font-semibold" style={{ color: 'var(--text-secondary)' }}>Traditional Offshore</th>
                  <th className="text-left p-4 font-semibold" style={{ color: 'var(--text-secondary)' }}>Onshore (US/UK)</th>
                </tr>
              </thead>
              <tbody>
                {OFFSHORE_COMPARISON.rows.map((r, i) => (
                  <tr key={i} className="border-t" style={{ borderColor: 'var(--border)' }}>
                    <td className="p-4 font-medium" style={{ color: 'var(--text-primary)' }}>{r.feature}</td>
                    <td className="p-4 font-semibold" style={{ background: 'rgba(234,88,12,0.06)', color: 'var(--text-primary)' }}>{r.us}</td>
                    <td className="p-4" style={{ color: 'var(--text-secondary)' }}>{r.traditional}</td>
                    <td className="p-4" style={{ color: 'var(--text-secondary)' }}>{r.onshore}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* HOW WE WORK */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading
            eyebrow="Engagement Process"
            title="From inquiry to onboarded team in 2-3 weeks."
          />
          <div className="mt-12">
            <ProcessFlowSVG steps={['Discovery', 'Sourcing', 'Interviews', 'Onboarding', 'Delivery']} />
          </div>
          <div className="mt-10 grid md:grid-cols-5 gap-4 max-w-5xl mx-auto">
            {[
              { t: 'Discovery', d: 'We understand your team, stack, and roles needed.' },
              { t: 'Sourcing', d: 'We shortlist top 1% engineers from our bench.' },
              { t: 'Interviews', d: 'You interview. We don\'t charge until they\'re onboarded.' },
              { t: 'Onboarding', d: 'Workstations, accounts, security training — all handled.' },
              { t: 'Delivery', d: 'Daily standups, weekly reports, AI-augmented velocity.' },
            ].map((s, i) => (
              <div key={i} className="text-center">
                <div className="font-heading text-lg font-bold mb-1" style={{ color: 'var(--text-primary)' }}>{s.t}</div>
                <div className="text-xs leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY INDIA */}
      <section className="py-20 lg:py-24 dark-section" style={{ background: 'var(--dark-section-bg)' }}>
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs font-semibold tracking-[0.2em] uppercase mb-4" style={{ color: 'var(--primary-accent)' }}>Why India for Offshore</div>
            <h2 className="font-heading text-3xl lg:text-5xl font-bold tracking-tight" style={{ color: '#F8FAFC' }}>
              India: the world's largest pool of senior software talent.
            </h2>
            <p className="mt-5 text-lg leading-relaxed" style={{ color: 'var(--dark-section-muted)' }}>
              India produces 1.5M+ engineering graduates annually. Top engineers from IITs, NITs, BITS, IIITs — many with 5-15 years at Microsoft, Google, Amazon, Goldman Sachs, and successful product startups. English-first. Strong overlap with US/UK time zones. World-class delivery centers in Bengaluru, Hyderabad, Pune, Chennai, and Gurgaon.
            </p>
            <ul className="mt-8 space-y-3">
              {[
                'Top 1% engineering talent at 30-50% of US cost',
                'Strong overlap with US Eastern (4-5 hrs) and West Coast (6-7 hrs)',
                'World-class delivery centers — Bengaluru, Hyderabad, Pune',
                'English-first communication',
                'GDPR, HIPAA, SOC 2, ISO 27001 — fully supported',
                'Strong IP and contract law (Indian Contracts Act)',
              ].map((p, i) => (
                <li key={i} className="flex items-start gap-3 text-base" style={{ color: '#F8FAFC' }}>
                  <CheckCircle weight="fill" size={20} className="mt-0.5 flex-shrink-0" style={{ color: 'var(--primary-accent)' }} />
                  {p}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl p-8 border" style={{ borderColor: 'var(--dark-section-border)', background: 'var(--dark-section-alt)' }}>
            <h3 className="font-heading text-xl font-bold mb-5" style={{ color: '#F8FAFC' }}>Where our engineers are based</h3>
            <div className="space-y-4">
              {[
                { city: 'Bengaluru', role: 'AI/ML, Full-stack, Mobile', count: '180+' },
                { city: 'Hyderabad', role: 'Cloud, DevOps, Backend', count: '120+' },
                { city: 'Pune', role: 'Enterprise (SAP, Oracle), QA', count: '90+' },
                { city: 'Chennai', role: 'Data Engineering, Analytics', count: '70+' },
                { city: 'Gurgaon', role: 'Frontend, UX, Product', count: '60+' },
              ].map((c, i) => (
                <div key={i} className="flex items-center justify-between py-3 border-b last:border-0" style={{ borderColor: 'var(--dark-section-border)' }}>
                  <div>
                    <div className="font-semibold text-sm" style={{ color: '#F8FAFC' }}>{c.city}</div>
                    <div className="text-xs" style={{ color: 'var(--dark-section-muted)' }}>{c.role}</div>
                  </div>
                  <div className="font-heading text-2xl font-bold" style={{ color: 'var(--primary-accent)' }}>{c.count}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CTABanner
        title="Ready to build your offshore team?"
        subtitle="Get a free proposal with engagement model recommendations, sample profiles, and cost estimates — in 60 seconds."
        ctaText="Request for Proposal"
        ctaHref="/start-project"
        secondaryText="Schedule a Call"
        secondaryHref="/contact"
      />

      <Footer />
    </div>
  );
}
